import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, TrendingUp, TrendingDown, ArrowRight, ArrowDown, ArrowUp, Search, RefreshCw, AlertTriangle, Inbox, Clock } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';

const FUEL_LABELS = {
  regular: 'Regular', super: 'Súper', diesel: 'Diesel', ulsd: 'ULSD',
  vpower: 'V-Power', premium: 'Premium',
};
const SVC_LABEL = { self: 'Auto', full: 'Full' };

const formatGTQ = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return `Q${Number(n).toFixed(2)}`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return '—'; }
};
const fmtRelativeMinutes = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `hace ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return `hace ${h}h${m ? ` ${m}m` : ''}`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d > 1 ? 's' : ''}`;
};

const DeltaPill = ({ delta }) => {
  if (!delta) return <span className="text-[#8a8579]">—</span>;
  const { direction, diff, pct } = delta;
  if (direction === 'flat') return <span className="text-[11px] text-[#8a8579]">sin cambio</span>;
  const up = direction === 'up';
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = up ? 'bg-[#fee2e2] text-[#991b1b] border-[#fecaca]' : 'bg-[#dcfce7] text-[#166534] border-[#bbf7d0]';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}>
      <Icon size={11}/>
      {up ? '+' : ''}{Number(diff).toFixed(2)} {pct !== null && pct !== undefined ? `(${up ? '+' : ''}${pct.toFixed(1)}%)` : ''}
    </span>
  );
};

const TABS = [
  { key: 'unreported', label: 'No reportados', icon: AlertTriangle },
  { key: 'changes',    label: 'Con cambios',   icon: TrendingUp },
];

const AdminPriceChanges = () => {
  const [tab, setTab] = useState('unreported');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // "Con cambios" state
  const [items, setItems] = useState([]);
  const [loadingChanges, setLoadingChanges] = useState(true);
  const [direction, setDirection] = useState('all');
  const [onlyChanged, setOnlyChanged] = useState(true);

  // "No reportados" state
  const [unreported, setUnreported] = useState([]);
  const [loadingUnrep, setLoadingUnrep] = useState(true);
  const [minMinutes, setMinMinutes] = useState(30);

  const loadChanges = () => {
    setLoadingChanges(true);
    api.get(`/prices/changes?only_changed=${onlyChanged}`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoadingChanges(false));
  };
  const loadUnrep = () => {
    setLoadingUnrep(true);
    api.get(`/prices/unreported?min_minutes=${minMinutes}`)
      .then(r => setUnreported(r.data || []))
      .catch(() => setUnreported([]))
      .finally(() => setLoadingUnrep(false));
  };

  useEffect(() => { loadChanges(); /* eslint-disable-next-line */ }, [onlyChanged]);
  useEffect(() => { loadUnrep();   /* eslint-disable-next-line */ }, [minMinutes]);

  // Helpers for date-range filtering
  const inRange = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    if (dateFrom) {
      const f = new Date(dateFrom + 'T00:00:00').getTime();
      if (t < f) return false;
    }
    if (dateTo) {
      const tt = new Date(dateTo + 'T23:59:59').getTime();
      if (t > tt) return false;
    }
    return true;
  };

  const filteredUnrep = useMemo(() => {
    const out = unreported.filter(it => {
      if (q && !(`${it.station_name} ${it.station_brand || ''}`.toLowerCase().includes(q.toLowerCase()))) return false;
      if ((dateFrom || dateTo) && !inRange(it.latest_marker_capture_at)) return false;
      return true;
    });
    // newest first
    out.sort((a, b) => (b.latest_marker_capture_at || '').localeCompare(a.latest_marker_capture_at || ''));
    return out;
  // eslint-disable-next-line
  }, [unreported, q, dateFrom, dateTo]);

  const fuelKeys = useMemo(() => {
    const set = new Set();
    items.forEach(it => Object.keys(it.deltas || {}).forEach(k => set.add(k)));
    return Array.from(set);
  }, [items]);
  const filteredChanges = useMemo(() => {
    const out = items.filter(it => {
      if (q && !(`${it.station_name} ${it.brand}`.toLowerCase().includes(q.toLowerCase()))) return false;
      if (direction !== 'all') {
        const dirs = Object.values(it.deltas || {}).map(d => d.direction);
        if (!dirs.includes(direction)) return false;
      }
      if ((dateFrom || dateTo) && !inRange(it.latest?.captured_at)) return false;
      return true;
    });
    out.sort((a, b) => (b.latest?.captured_at || '').localeCompare(a.latest?.captured_at || ''));
    return out;
  // eslint-disable-next-line
  }, [items, q, direction, dateFrom, dateTo]);

  const summary = useMemo(() => {
    let up = 0, down = 0;
    items.forEach(it => Object.values(it.deltas || {}).forEach(d => {
      if (d.direction === 'up') up++;
      else if (d.direction === 'down') down++;
    }));
    return { up, down, stations: items.length };
  }, [items]);

  const refresh = () => { if (tab === 'unreported') loadUnrep(); else loadChanges(); };

  return (
    <div className="space-y-6" data-testid="admin-price-changes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Diferencias de precios</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">
            {tab === 'unreported'
              ? `Estaciones que detectaron diferencias en sus marcadores pero no han enviado tracking · ${unreported.length}`
              : `Estaciones cuya última captura difiere de la anterior · ${summary.stations} estaciones · ${summary.up} subidas · ${summary.down} bajadas`}
          </p>
        </div>
        <Button onClick={refresh} variant="outline" className="h-10 rounded-full border-[#d4cfc4] text-[12.5px]" data-testid="changes-refresh">
          <RefreshCw size={13} className="mr-1.5"/> Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <div className="inline-flex gap-1 bg-white border border-[#e8e5de] rounded-full p-1">
        {TABS.map(t => {
          const Icon = t.icon;
          const badge = t.key === 'unreported' ? unreported.length : items.length;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12.5px] transition ${active ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'text-[#5a5a5a] hover:bg-[#f3f0e9]'}`}
            >
              <Icon size={13} strokeWidth={1.7}/>
              <span>{t.label}</span>
              {badge > 0 && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${active ? 'bg-[#f8f6f1] text-[#1c1c1c]' : (t.key === 'unreported' ? 'bg-[#b91c1c] text-white' : 'bg-[#e8e5de] text-[#3a3a3a]')}`}>
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar estación o marca…" className="pl-9 bg-white border-[#e8e5de] rounded-full text-[13px] h-10" data-testid="changes-search"/>
        </div>
        <div className="flex items-center gap-1.5 text-[11.5px] text-[#5a5a5a]">
          <span className="uppercase tracking-wider text-[10px] text-[#8a8579]">Desde</span>
          <Input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="h-10 w-[140px] bg-white border-[#e8e5de] rounded-full text-[12px] px-3" data-testid="filter-date-from"/>
          <span className="uppercase tracking-wider text-[10px] text-[#8a8579] ml-1">Hasta</span>
          <Input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="h-10 w-[140px] bg-white border-[#e8e5de] rounded-full text-[12px] px-3" data-testid="filter-date-to"/>
          {(dateFrom || dateTo) && (
            <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[11px] underline text-[#8a8579] hover:text-[#1c1c1c]" data-testid="clear-dates">Limpiar</button>
          )}
        </div>
        {tab === 'changes' && (
          <>
            <div className="flex items-center gap-1 bg-white border border-[#e8e5de] rounded-full p-1">
              {[{ v: 'all', label: 'Todos' }, { v: 'up', label: 'Subidas' }, { v: 'down', label: 'Bajadas' }].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setDirection(opt.v)}
                  data-testid={`filter-direction-${opt.v}`}
                  className={`px-3 py-1.5 rounded-full text-[12px] transition ${direction === opt.v ? 'bg-[#1c1c1c] text-[#f8f6f1]' : 'text-[#3a3a3a] hover:bg-[#f3f0e9]'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-[12px] text-[#5a5a5a] cursor-pointer">
              <input type="checkbox" checked={onlyChanged} onChange={e=>setOnlyChanged(e.target.checked)} className="w-4 h-4" data-testid="filter-only-changed"/>
              Sólo con cambios
            </label>
          </>
        )}
        {tab === 'unreported' && (
          <div className="flex items-center gap-1.5 text-[11.5px] text-[#5a5a5a]">
            <Clock size={12} className="text-[#8a8579]"/>
            <span>Umbral:</span>
            <Input type="number" min={1} max={1440} value={minMinutes} onChange={e=>setMinMinutes(Math.max(1, Math.min(1440, parseInt(e.target.value || '30', 10))))} className="h-10 w-[80px] bg-white border-[#e8e5de] rounded-full text-[12px] tabular-nums text-right pr-3" data-testid="filter-min-minutes"/>
            <span>min</span>
          </div>
        )}
      </div>

      {/* Content */}
      {tab === 'unreported' ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
          {loadingUnrep ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
          ) : filteredUnrep.length === 0 ? (
            <div className="py-16 text-center">
              <Inbox size={28} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
              <p className="text-[14px] text-[#5a5a5a]">Sin estaciones pendientes de reportar tracking.</p>
              <p className="text-[12px] text-[#8a8579] mt-1">Todas las diferencias detectadas han sido procesadas.</p>
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[#f8f6f1]">
                <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                  <th className="py-3 px-4 font-normal">Estación</th>
                  <th className="py-3 px-3 font-normal">Diferencias detectadas</th>
                  <th className="py-3 px-3 font-normal">Última captura</th>
                  <th className="py-3 px-3 font-normal">Última solicitud</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e5de]">
                {filteredUnrep.map(it => (
                  <tr key={it.station_id} className="hover:bg-[#fafaf6]" data-testid={`unrep-row-${it.station_id}`}>
                    <td className="py-3 px-4">
                      <p className="font-medium leading-tight">{it.station_name}</p>
                      <p className="text-[11px] text-[#8a8579]">{it.station_brand} · {it.region}</p>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex flex-wrap gap-1.5 max-w-[440px]">
                        {(it.top_diffs || []).map((d) => {
                          const lower = d.delta < 0;
                          return (
                            <span key={`${d.fuel}-${d.svc}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono tabular-nums ${lower ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#dcfce7] text-[#166534]'}`}>
                              {lower ? <ArrowDown size={10}/> : <ArrowUp size={10}/>}
                              {FUEL_LABELS[d.fuel] || d.fuel}·{SVC_LABEL[d.svc]} Q{Math.abs(Number(d.delta)).toFixed(2)}
                            </span>
                          );
                        })}
                        {it.diffs_count > (it.top_diffs || []).length && (
                          <span className="text-[10.5px] text-[#8a8579] self-center">+{it.diffs_count - it.top_diffs.length} más</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[12px] text-[#5a5a5a]">
                      <p>{fmtDate(it.latest_marker_capture_at)}</p>
                      <p className="text-[11px] text-[#8a8579]">{fmtRelativeMinutes(it.minutes_since_latest_capture)}</p>
                    </td>
                    <td className="py-3 px-3 text-[12px] text-[#5a5a5a]">
                      {it.last_request_at ? (
                        <>
                          <p>{fmtDate(it.last_request_at)}</p>
                          <p className="text-[11px] text-[#8a8579]">{it.last_request_status}</p>
                        </>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] text-[11px]">Nunca</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#e8e5de] rounded-xl overflow-hidden">
          {loadingChanges ? (
            <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
          ) : filteredChanges.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-[14px] text-[#5a5a5a]">No hay estaciones con diferencias de precios para mostrar.</p>
              <p className="text-[12px] text-[#8a8579] mt-1">Se necesitan al menos 2 capturas por estación para detectar cambios.</p>
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[#f8f6f1]">
                <tr className="text-left text-[11px] tracking-wider uppercase text-[#8a8579] border-b border-[#e8e5de]">
                  <th className="py-3 px-4 font-normal">Estación</th>
                  {fuelKeys.map(k => (
                    <th key={k} className="py-3 px-3 font-normal">{FUEL_LABELS[k] || k}</th>
                  ))}
                  <th className="py-3 px-3 font-normal">Última captura</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e5de]">
                {filteredChanges.map(it => (
                  <tr key={it.station_id} className="hover:bg-[#fafaf6]" data-testid={`row-station-${it.station_id}`}>
                    <td className="py-3 px-4">
                      <p className="font-medium leading-tight">{it.station_name}</p>
                      <p className="text-[11px] text-[#8a8579]">{it.brand}</p>
                    </td>
                    {fuelKeys.map(k => {
                      const d = (it.deltas || {})[k];
                      return (
                        <td key={k} className="py-3 px-3">
                          {d ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-[11.5px] text-[#5a5a5a]">
                                <span className="text-[#8a8579]">{formatGTQ(d.previous)}</span>
                                <ArrowRight size={10} className="text-[#8a8579]"/>
                                <span className="font-medium text-[#1c1c1c]">{formatGTQ(d.latest)}</span>
                              </div>
                              <DeltaPill delta={d}/>
                            </div>
                          ) : (
                            <span className="text-[#cfcabe] text-[11px]">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="py-3 px-3 text-[12px] text-[#5a5a5a]">
                      <p>{fmtDate(it.latest?.captured_at)}</p>
                      <p className="text-[11px] text-[#8a8579]">por {it.latest?.captured_by_name || '—'}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPriceChanges;
